/**
 * Counted phrases, per language plural rules (Intl.PluralRules).
 *
 * English uses `one` / `other`. Arabic uses all six forms, so a count is never
 * glued to one static noun ("٤ فرع" is wrong; "أربعة فروع" is right):
 *   zero  — لا توجد فروع
 *   one   — فرع واحد          two  — فرعان
 *   few   — 3–10  → «{wordsM} فروع» (أربعة فروع) or «{wordsF} مدن» (أربع مدن)
 *   many  — 11–99 → «{count} فرعًا»
 *   other — 100+  → «{count} فرع»
 * Placeholders: {count} is the formatted number; {wordsM} / {wordsF} are the
 * Arabic number words for 3–10 when the counted noun is masculine / feminine
 * (the numeral takes the opposite gender); outside 3–10 they fall back to digits.
 */
export interface PluralForms {
  readonly one: string;
  readonly other: string;
  readonly zero?: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
}

/** Declares a counted phrase in a message catalogue (keeps the optional Arabic forms in the type). */
export function plural(forms: PluralForms): PluralForms {
  return forms;
}

export function isPluralForms(value: unknown): value is PluralForms {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).one === 'string' &&
    typeof (value as Record<string, unknown>).other === 'string'
  );
}

const WORDS_FOR_MASCULINE_NOUN: Record<number, string> = {
  3: 'ثلاثة', 4: 'أربعة', 5: 'خمسة', 6: 'ستة', 7: 'سبعة', 8: 'ثمانية', 9: 'تسعة', 10: 'عشرة',
};
const WORDS_FOR_FEMININE_NOUN: Record<number, string> = {
  3: 'ثلاث', 4: 'أربع', 5: 'خمس', 6: 'ست', 7: 'سبع', 8: 'ثماني', 9: 'تسع', 10: 'عشر',
};

const rulesCache = new Map<string, Intl.PluralRules>();

export function pluralCategory(count: number, locale: string): keyof PluralForms {
  let rules = rulesCache.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    rulesCache.set(locale, rules);
  }
  return rules.select(count) as keyof PluralForms;
}

export function formatCounted(
  forms: PluralForms,
  count: number,
  locale: string,
  formattedCount: string,
  values: Record<string, string | number> = {},
): string {
  const template = forms[pluralCategory(count, locale)] ?? forms.other;
  const all: Record<string, string | number> = {
    ...values,
    count: formattedCount,
    wordsM: WORDS_FOR_MASCULINE_NOUN[count] ?? formattedCount,
    wordsF: WORDS_FOR_FEMININE_NOUN[count] ?? formattedCount,
  };
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(all, key) ? String(all[key]) : match,
  );
}
